import React, { useEffect, useState } from 'react';
import { BarChart3, RefreshCw, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PARTNER_NAMES = {
  safetywing: 'SafetyWing',
  airalo: 'Airalo',
  wise: 'Wise',
  airbnb: 'Airbnb',
  coworking: 'Coworking Network',
  healthcare: 'Healthcare Network',
  movers: 'International Movers',
  storage: 'Storage Solutions',
  schools: 'Bilingual Schools',
  pets: 'Pet Relocation',
};

/**
 * Admin → Discounts tab.
 *
 * Per-partner click counts pulled from /api/admin/discounts/click-stats.
 * Tracks every CTA click fired through `trackDiscountClicked` in the front-end
 * (which mirrors the GA4 event to /api/discounts/track-click).
 */
export default function AdminDiscountsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/discounts/click-stats`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setErr(e.message || 'Could not load click stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago'});
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="admin-discounts-panel" className="space-y-6">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-1">
            Admin · Marketing
          </p>
          <h2
            className="font-display italic text-3xl text-[#0A1F2E] leading-tight"
            data-testid="admin-discounts-headline"
          >
            Partner discount clicks
          </h2>
          <p className="font-body text-sm text-[#0A1F2E]/65 mt-1">
            Total clicks · this week · this month. Mirrored from GA4 `discount_clicked` events.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="admin-discounts-refresh"
          className="inline-flex items-center gap-2 border border-[#0A1F2E]/15 hover:border-[#2CA8A0] text-[#0A1F2E] font-mono uppercase text-[10px] tracking-[0.15em] py-2.5 px-4 rounded-full transition disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </header>

      {err && (
        <p className="font-body text-sm text-red-700 bg-red-50 border-l-4 border-red-400 px-4 py-2 rounded-r">
          {err}
        </p>
      )}

      <section
        className="bg-white border border-[#EFE6CF] rounded-xl overflow-hidden"
        data-testid="admin-discounts-stats"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#EFE6CF] bg-[#F9F6EF]">
          <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/70">
            <BarChart3 className="w-3 h-3 inline mr-1.5 -mt-0.5" />
            Total clicks: <span className="text-[#0A1F2E] font-semibold">{data?.total_clicks ?? 0}</span>
          </p>
          {data?.as_of && (
            <p className="font-mono uppercase text-[9px] tracking-[0.15em] text-[#0A1F2E]/45">
              As of {formatDate(data.as_of)}
            </p>
          )}
        </div>

        <table className="w-full text-sm">
          <thead className="bg-[#F4ECD8]/40">
            <tr>
              <th className="text-left font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/65 px-5 py-3">Partner</th>
              <th className="text-right font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/65 px-5 py-3">Total</th>
              <th className="text-right font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/65 px-5 py-3">This week</th>
              <th className="text-right font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/65 px-5 py-3">This month</th>
              <th className="text-right font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/65 px-5 py-3">Last click</th>
            </tr>
          </thead>
          <tbody>
            {(data?.stats || []).length === 0 ? (
              <tr>
                <td colSpan="5" className="px-5 py-10 text-center font-body text-[#0A1F2E]/55 italic">
                  No clicks recorded yet.
                </td>
              </tr>
            ) : (
              (data?.stats || []).map((s) => (
                <tr
                  key={s.partner_id}
                  data-testid={`admin-discounts-row-${s.partner_id}`}
                  className="border-t border-[#EFE6CF]/60"
                >
                  <td className="px-5 py-3.5 font-body text-[#0A1F2E]">
                    <span className="flex items-center gap-2">
                      {PARTNER_NAMES[s.partner_id] || s.partner_id}
                      <ExternalLink className="w-3 h-3 text-[#0A1F2E]/30" />
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right font-mono font-semibold text-[#0A1F2E]">{s.total}</td>
                  <td className="px-5 py-3.5 text-right font-mono text-[#0A1F2E]/75">{s.this_week}</td>
                  <td className="px-5 py-3.5 text-right font-mono text-[#0A1F2E]/75">{s.this_month}</td>
                  <td className="px-5 py-3.5 text-right font-mono text-xs text-[#0A1F2E]/55">{formatDate(s.last_click_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
